import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FileUpload,
    IconButton,
    ResourceChip,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { uploadFile } from 'utils/file';
import { getPrettyFileTypes } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
];

const UploadAssignmentsDialog = ({ open, assignment, multiple = true, onClose, onClick }) => {
    const { course } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();

    const [files, setFiles] = useState(null);

    const handleClose = () => {
        if (files?.some((file) => file.loading)) return;

        setTimeout(() => {
            setFiles(null);
        }, 150);
        onClose();
    };

    const handleChange = async (files) => {
        if (!multiple && files.length > 1) return message.error('Please select a single file.');

        if (!files.length) {
            return setDialog({
                open: true,
                message: 'Error Detected',
                description:
                    'Oops! There was an error uploading the file. Please make sure the file you are trying to upload is not in a ZIP folder.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Ok',
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        const timestamp = new Date().getTime();
        files = Array.from(files).map((file) => ({
            id: uuid(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: assignment.id,
            data: {
                name: file.name,
                type: file.type,
                size: file.size,
            },
            fileData: file,
            loading: true,
            loadingValue: 0,
        }));
        setFiles(files);

        if (
            files.some((file) => {
                const fileType = file.data.type;
                return !ALLOWED_FILE_TYPES.includes(fileType);
            })
        ) {
            setDialog({
                open: true,
                message: 'Upload Failed Due to Unsupported Files',
                description: `Timelygrader only supports ${getPrettyFileTypes(ALLOWED_FILE_TYPES)} files. Please ensure the files you are uploading are all in one of the accepted formats and try uploading again.`,
                note: '',
                variant: 'destructive',
                actionButtonText: 'Ok',
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
            return setFiles(null);
        }

        const upload = (file, onProgress) => {
            if (!file.data.size) throw new Error('File size is 0.');
            return new Promise((resolve, reject) => {
                uploadFile(
                    file.fileData,
                    ({ percent }) => onProgress(percent),
                    (data) => resolve(data),
                    (error) => {
                        console.error(error);
                        reject(error);
                    },
                    auth.owner_account_id
                );
            });
        };

        for (const file of files.filter((file) => file.loading)) {
            try {
                const data = await upload(file, (percent) => {
                    setFiles((current) => {
                        return current.map((currentFile) => {
                            return currentFile.id === file.id
                                ? {
                                      ...currentFile,
                                      loadingValue: Math.round(percent),
                                  }
                                : currentFile;
                        });
                    });
                });
                setFiles((current) => {
                    return current.map((currentFile) => {
                        return currentFile.id === file.id
                            ? {
                                  ...omit(currentFile, ['data', 'fileData']),
                                  data: {
                                      ...currentFile.data,
                                      ...data,
                                  },
                                  loading: false,
                              }
                            : currentFile;
                    });
                });
            } catch (e) {
                console.log(e);
                setDialog({
                    open: true,
                    message: 'Error Detected',
                    description:
                        'Oops! There was an error uploading the file. Please make sure the file you are trying to upload is not in a ZIP folder.',
                    note: '',
                    variant: 'destructive',
                    actionButtonText: 'Ok',
                    cancelButtonText: '',
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
                return setFiles(null);
            }
        }
    };

    const handleSelect = ({ key }, file) => {
        if (key === 'delete') {
            if (files.length === 1 && files[0].id === file.id) {
                setFiles(null);
            } else {
                setFiles((current) => current.filter(({ id }) => id !== file.id));
            }
        }
    };

    const handleClickUpload = () => {
        onClick(files.map((file) => omit(file, ['loading', 'loadingValue'])));
        handleClose();
    };

    return (
        <CustomDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        Upload Submission{multiple ? 's' : ''}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        disabled={files?.some((file) => file.loading)}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                {auth.role === 'instructor' && (
                    <Text variant={'p3'} weight={'medium'}>
                        You can upload up to 600 files at a time
                    </Text>
                )}
            </DialogTitle>
            <Divider />
            <DialogContent>
                {files?.length ? (
                    <Stack spacing={2}>
                        {files.map((file) => (
                            <ResourceChip
                                key={file.id}
                                options={[{ key: 'delete', value: 'Delete' }]}
                                resource={file}
                                size={'medium'}
                                type={'file'}
                                onSelect={handleSelect}
                            />
                        ))}
                    </Stack>
                ) : (
                    <FileUpload accept={ALLOWED_FILE_TYPES} multiple={multiple} onChange={handleChange} />
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'center'}
                    direction={'row-reverse'}
                    justifyContent={'space-between'}
                >
                    <Button disabled={!files || files.some((file) => file.loading)} onClick={handleClickUpload}>
                        Upload
                    </Button>
                    {files && (
                        <Text color={'light'}>
                            {files.filter((file) => !file.loading).length}/{files.length} added
                        </Text>
                    )}
                </Stack>
            </DialogActions>
        </CustomDialog>
    );
};

const CustomDialog = styled(Dialog)(() => {
    return {
        '& .MuiDialog-paper': {
            width: '400px',
            height: '500px',
        },
    };
});

export default UploadAssignmentsDialog;
